import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Input size variants.
 *
 * Every variant is `h-11 md:h-N` so mobile preserves the 44px touch-target
 * minimum (DESIGN-TOKENS §4) while desktop gets the compact treatment.
 * NEVER pass a bare `h-8`/`h-9` at a call site — tailwind-merge only dedupes
 * within matching modifier groups, so a bare height displaces the `h-11` and
 * leaves the `md:` half standing: the desktop override silently no-ops AND
 * the mobile touch target is destroyed. Go through the variant.
 *
 * Desktop band: 32px default / 28px sm / 40px lg. Measured against
 * marketer.com (28–32px controls, read off a DPR-2 capture) and shadcn
 * (`h-9`); 403 of the repo's 728 hardcoded control heights were already
 * <=32px before this default moved, so the primitive was the outlier.
 */
const inputVariants = cva(
  cn(
    // `dark:bg-input` — see the matching note on `selectTriggerVariants` in
    // `select.tsx`: the field needs its own explicit surface in dark mode
    // because `--input` sits LIGHTER than `--background`/`--card` there (a
    // raised field), while in light mode `--input` sits darker than the page
    // and this control stays flush with it. Light mode is unchanged.
    'flex w-full rounded-[var(--radius)] border border-border bg-background dark:bg-input',
    'ring-offset-background',
    'file:border-0 file:bg-transparent file:font-medium file:text-foreground',
    'placeholder:text-muted-foreground',
    // `shadow-sm`/`shadow-ring` are deliberately absent here: every
    // `--shadow-*` token in this file resolves to a fully transparent,
    // zero-geometry value (see `--shadow-sm` near the top of index.css), so a
    // `shadow-sm shadow-ring/10` pair renders NOTHING — measured live,
    // 2026-08-26: the computed box-shadow layer it contributes is
    // `0px 0px 0px 0px`. It is dead weight that silently reactivates as a
    // colored blur the moment someone un-zeroes the shadow scale. The real,
    // visible focus signal is the border tint + the 1px ring below.
    'focus-visible:outline-none focus-visible:border-ring/60 focus-visible:ring-1 focus-visible:ring-ring/30',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'transition duration-200 ease-out',
  ),
  {
    variants: {
      size: {
        // Standard form input — sign-up, primary onboarding, full-page forms
        default: 'h-11 md:h-8 px-2.5 py-1 text-base md:text-sm file:text-sm',
        // Dense dialogs, settings sub-forms, side rails — 28px on desktop,
        // 44px touch-target on mobile
        sm: 'h-11 md:h-7 px-2 py-0.5 text-base md:text-sm file:text-sm',
        // Hero / marketing — rare
        lg: 'h-12 md:h-10 px-3 py-2 text-base file:text-sm',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export interface InputProps
  extends Omit<React.ComponentProps<'input'>, 'size'>, VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input type={type} className={cn(inputVariants({ size }), className)} ref={ref} {...props} />
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
